import { ChangeDetectorRef, Component, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { ActivatedRoute, Router } from '@angular/router';
import * as _ from 'lodash';
import * as moment from 'moment';
import { EMPTY, Subscription, switchMap, takeUntil } from 'rxjs';
import { PatientAiService } from 'src/app/core/services/patient-ai.service';
import { BaseComponent } from './../../../../core/components/base/base.component';
import { patientGroupId } from './../../../../core/utils/patient-group';
import { ConsentFormsService } from './../../../../core/services/consent-forms.service';
import { FeedbackService } from './../../../../core/services/feedback.service';
import { GroupService } from './../../../../core/services/group.service';
import { PatientConsentService } from './../../../../core/services/patient-consent.service';
import { PatientInviteService } from './../../../../core/services/patient-invite.service';
import { PatientService } from './../../../../core/services/patient.service';
import { UserService } from './../../../../core/services/user.service';
import { SignaturePadComponent } from './../../../../shared/components/signature-pad/signature-pad.component';
import { isStateModeGroup } from 'src/app/shared/util/state-mode.util';


@Component({
  selector: 'app-patient-consent-detail',
  templateUrl: './patient-consent-detail.component.html',
  styleUrls: ['./patient-consent-detail.component.scss']
})
export class PatientConsentDetailComponent extends BaseComponent implements OnInit {
  @ViewChildren(MatMenuTrigger) menuTriggers!: QueryList<MatMenuTrigger>;
  todayDate: moment.Moment = moment();
  form!: FormGroup;
  editingConsent: any;
  patient: any = {};
  groupId!: string;
  consentTypes: any[] = [];
  patientAiRequests: any[] = [];
  /** AI requests with detected procedure/condition/risks, newest first (interactive consent only). */
  eligibleAiRequestsForConsent: any[] = [];
  /** Which AI request supplies pre-filled interactive fields. */
  selectedAiRequestIdForConsent: string | null = null;
  selectedConsentForm: any = null;
  @ViewChild(SignaturePadComponent) signaturePadComponent!: SignaturePadComponent;
  /** Template `procedureTitle` for interactive consent when AI does not supply a title. */
  private interactiveConsentProcedureTitleFallback = '';
  groupUsers: any[] = [];

  private routeParamsSub!: Subscription;

  /** A state unit (tasks/state-mode.md): consents are signed and saved here, never sent. */
  get stateMode(): boolean {
    return isStateModeGroup(this.groupService.group);
  }

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private userService: UserService,
    private groupService: GroupService,
    private patientService: PatientService,
    private consentService: PatientConsentService,
    private consentFormsService: ConsentFormsService,
    private inviteService: PatientInviteService,
    private patientAIService: PatientAiService,
    private cd: ChangeDetectorRef,
    fs: FeedbackService,
    md: MatDialog
  ) {
    super(fs, md);
  }

  ngOnInit() {
    this.initForm();

    this.routeParamsSub = this.route.paramMap.subscribe(params => {
      const id = params.get('id');
      if (id) {
        this.consentService.getConsentById(id)
          .pipe(
            takeUntil(this.destroy$)
          ).subscribe();
      } else {
        this.resetForm();
      }
    });

    this.groupService.selectedGroup$
      .pipe(
        switchMap(selectedGroup => {
          if (selectedGroup) {
            this.groupId = selectedGroup.id;
            this.form.patchValue({ groupId: patientGroupId(this.patient, this.groupId) });
          }
          return EMPTY;
        }),
        takeUntil(this.destroy$)
      ).subscribe();


    this.patientService.currentPatient$
      .pipe(
        switchMap(selectedPatient => {
          if (selectedPatient) {
            this.patient = selectedPatient;
            this.form.patchValue({ patientId: this.patient.id, groupId: patientGroupId(this.patient, this.groupId) });
          }
          return EMPTY;
        }),
        takeUntil(this.destroy$)
      ).subscribe();

    this.groupService.groupUsers$
      .pipe(takeUntil(this.destroy$))
      .subscribe((users: any[]) => {
        this.groupUsers = users?.filter(u => u.userType === 'doctor' && u.userId !== '511f39e1-ff14-4a6d-b35a-6fcc22923b50');
        this.patchPhysician();
      });

    this.consentService.editingConsent$
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(data => {
        this.editingConsent = data;
        this.patchForm();
      });

    this.consentFormsService.consentForms$
      .pipe(
        takeUntil(this.destroy$)
      ).subscribe((consentForms) => {
        if (consentForms) {
          this.consentTypes = consentForms;
        }
      });

    this.patientAIService.patientAIRequests$
      .pipe(takeUntil(this.destroy$),).subscribe({
        next: (results) => {
          this.patientAiRequests = results || [];
          this.refreshEligibleAiRequestsForConsent();
        },
        error: (err) => {
          console.error('Error loading AI requests:', err);
          this.patientAiRequests = [];
          this.refreshEligibleAiRequestsForConsent();
        }
      });
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    if (this.routeParamsSub) {
      this.routeParamsSub.unsubscribe();
    }
  }

  initForm() {
    this.form = this.fb.group({
      date: [moment(), Validators.required],
      groupId: [patientGroupId(this.patient, this.groupId), Validators.required],
      patientId: [this.patient.id, Validators.required],
      consentType: [''],
      consentFormId: ['', Validators.required],
      consentGiven: [true, Validators.required],
      signature: ['', Validators.required],
      physicianId: ['', Validators.required],
    });
  }

  patchForm() {
    if (!this.editingConsent) {
      return; // Guard clause in case this.editingNote is not yet set
    }

    // Basic patching for directly matched fields
    this.form.patchValue(this.editingConsent);


    // For any other special handling or transformations, continue below
    if (this.editingConsent.date) {
      this.form.patchValue({ date: moment(this.editingConsent.date, 'DD/MM/YYYY') });
    }

  }

  private patchPhysician() {
    const currentUser = this.userService.user;
    const groupUsers = this.groupUsers || this.groupService.groupUsers;
    const groupOwner = this.groupService.getGroupOwner();
    if (groupUsers.findIndex(gu => gu.userId === currentUser.id) > -1) {
      this.form.patchValue({ physicianId: currentUser.id });
    } else if (groupOwner) {
      this.form.patchValue({ physicianId: groupOwner.userId });
    } else {
      this.form.patchValue({ physicianId: groupUsers[0].userId })
    }
  }

  selectConsentType(event: any) {
    const consentType = { ...event.value };

    if (consentType.conditionalConsent) {
      consentType.conditionalConsent = [...consentType.conditionalConsent].map((c: any) => { return { consentGiven: true, name: c } }) || [];
    }
    if (consentType.informationSheets) {
      consentType.informationSheets = consentType.informationSheets.map((sheet: any) => { return { ...sheet, sendSheet: true } }) || []
    }

    if (consentType.consentType === 'interactive') {
      this.interactiveConsentProcedureTitleFallback = consentType.procedureTitle || '';
      this.eligibleAiRequestsForConsent = this.getEligibleAiRequestsForConsent();
      this.selectedAiRequestIdForConsent = this.eligibleAiRequestsForConsent[0]?.id ?? null;
    } else {
      this.interactiveConsentProcedureTitleFallback = '';
      this.eligibleAiRequestsForConsent = [];
      this.selectedAiRequestIdForConsent = null;
    }

    consentType.selectedRequiredConsent = consentType.additionalRequiredConsent ? consentType.additionalRequiredConsent[0] : '';
    consentType.selectedProcedureSide = '';
    consentType.selectedImplant = '';
    consentType.selectedTransfusion = '';


    this.selectedConsentForm = {
      ...consentType,
      procedureTitle: '',
      conditionText: '',
      specificRisksText: ''
    };

    if (consentType.consentType === 'interactive') {
      const req = this.eligibleAiRequestsForConsent.find(r => r.id === this.selectedAiRequestIdForConsent);
      this.applyInteractiveDetectedFieldsFromRequest(req);
    } else {
      this.selectedConsentForm.procedureTitle = consentType.procedureTitle || '';
    }

    setTimeout(() => {
      this.form.patchValue({ consentFormId: consentType.id });
      this.cd.detectChanges();
    });
  }

  private getEligibleAiRequestsForConsent(): any[] {
    const requests = this.patientAiRequests.filter(req => req.detectedProcedureTitle || req.detectedCondition || req.detectedRisks);
    return _.chain(requests)
      .orderBy(i => i.createdAt, 'desc')
      .value();
  }

  private applyInteractiveDetectedFieldsFromRequest(request: any | null | undefined) {
    if (!this.selectedConsentForm || this.selectedConsentForm.consentType !== 'interactive') {
      return;
    }
    if (!request) {
      this.selectedConsentForm.procedureTitle = this.interactiveConsentProcedureTitleFallback || '';
      this.selectedConsentForm.conditionText = '';
      this.selectedConsentForm.specificRisksText = '';
      return;
    }
    this.selectedConsentForm.procedureTitle = request.detectedProcedureTitle || this.interactiveConsentProcedureTitleFallback || '';
    this.selectedConsentForm.conditionText = request.detectedCondition || '';
    this.selectedConsentForm.specificRisksText = request.detectedRisks || '';
  }

  private refreshEligibleAiRequestsForConsent() {
    if (this.selectedConsentForm?.consentType !== 'interactive') {
      return;
    }
    const eligible = this.getEligibleAiRequestsForConsent();
    this.eligibleAiRequestsForConsent = eligible;
    if (!eligible.length) {
      this.selectedAiRequestIdForConsent = null;
      this.applyInteractiveDetectedFieldsFromRequest(null);
      this.cd.detectChanges();
      return;
    }
    const prevId = this.selectedAiRequestIdForConsent;
    if (prevId && eligible.some(r => r.id === prevId)) {
      // keep user's selection
    } else {
      this.selectedAiRequestIdForConsent = eligible[0].id;
    }
    const req = eligible.find(r => r.id === this.selectedAiRequestIdForConsent);
    this.applyInteractiveDetectedFieldsFromRequest(req);
    this.cd.detectChanges();
  }

  onSelectedAiRequestForConsentChange(id: string | null) {
    const req = this.eligibleAiRequestsForConsent.find(r => r.id === id);
    this.applyInteractiveDetectedFieldsFromRequest(req);
  }

  resetForm() {
    setTimeout(() => {
      this.editingConsent = null;
      this.selectedConsentForm = [];
      this.eligibleAiRequestsForConsent = [];
      this.selectedAiRequestIdForConsent = null;
      this.interactiveConsentProcedureTitleFallback = '';
      this.form.reset();
      this.initForm();
      this.patchPhysician();
      this.cd.detectChanges();
    });
  }

  getSignatureData(base64Image: any) {
    // Store the base64 image directly in the form
    this.form.patchValue({
      signature: base64Image
    });
  }

  private dataURLToBlob(dataURL: string) {
    const byteString = atob(dataURL.split(",")[1]);
    const mimeString = dataURL.split(",")[0].split(":")[1].split(";")[0];

    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);

    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }

    return new Blob([ia], { type: mimeString });
  }

  /**
   * §29. The attachment picker's selection, posted with the request.
   *
   * ABSENT vs EMPTY is load-bearing on the server: `undefined` means no picker was involved and
   * the practice's `sendWithInvite` configuration decides, while `[]` means the user unticked
   * everything and nothing is raised. This is always an array because this screen always has the
   * picker — so an untick here genuinely means "do not send it", which is the whole point of
   * §14.3's "configuration pre-ticks; it never forces".
   */
  selectedDocumentIds: string[] = [];

  sendConsent() {

    const date: moment.Moment = this.form.value.date;

    const consentText = this.compileConsentText(false);

    const data = {
      ...this.form.value,
      formDetails: this.selectedConsentForm,
      consentGiven: false,
      email: this.patient.email,
      date: date.utc().startOf('day').toISOString(),
      consentText,
      practiceDocumentIds: this.selectedDocumentIds
    };

    const formData = new FormData();
    delete data.signature;
    formData.append('data', JSON.stringify(data));

    const getMostRecentConsentId = (consents: any[]) => {
      if (!consents?.length) return;
      const sorted = _.chain(consents)
        .orderBy(i => i.createdAt, 'desc')
        .value();
      return sorted[0].id;
    }

    this.consentService.addConsent(formData)
      .pipe(
        takeUntil(this.destroy$),
        switchMap((consent) => this.inviteService.addInvite(data, true, [getMostRecentConsentId(consent.data)]))
      )
      .subscribe({
        next: (result) => {
          this.showToastMessage('Patient consent sent successfully');
          this.closeAllMenus(this.menuTriggers);
          this.returnToParent();
        },
        error: (error) => {
          console.error('Error sending patient consent: ', error);
          this.showToastMessage('There was an error sending the patient consent. Please try again.');
        }
      });

  }

  compileConsentText(includeAdditional: boolean = true): string {

    let result = '';
    if (this.selectedConsentForm.consentType === 'standard') {
      result = this.consentFormsService.compileStandardConsentResult(this.selectedConsentForm, includeAdditional);
    }

    if (this.selectedConsentForm.consentType === 'interactive') {
      result = this.consentFormsService.compileInteractiveConsentResult(this.selectedConsentForm, includeAdditional);
    }

    return result;
  }


  compileConsentResult(): string {

    let result = '';
    if (this.selectedConsentForm.consentType === 'standard') {
      result = this.consentFormsService.compileStandardConsentResult(this.selectedConsentForm, true);
    }

    if (this.selectedConsentForm.consentType === 'interactive') {
      result = this.consentFormsService.compileInteractiveConsentResult(this.selectedConsentForm, true);
    }

    return result;
  }

  saveConsent() {

    if (!this.signaturePadComponent.isValid()) {
      this.showToastMessage('Please provide your signature and complete the signatory information.');
      return;
    }

    this.form.patchValue({
      signature: this.signaturePadComponent.getPadData()
    });

    setTimeout(() => {

      if (this.selectedConsentForm.confirmSide && !this.selectedConsentForm.selectedProcedureSide) {
        this.showToastMessage('Please select a procedure side to continue');
        return;
      }

      if (this.selectedConsentForm.confirmImplant && !this.selectedConsentForm.selectedImplant) {
        this.showToastMessage('Please select an implant to continue');
        return;
      }

      if (this.selectedConsentForm.confirmTransfusion && !this.selectedConsentForm.selectedTransfusion) {
        this.showToastMessage('Please select a blood transfusion option to continue');
        return;
      }


      if (this.selectedConsentForm.additionalRequiredConsent?.length && !this.selectedConsentForm.selectedRequiredConsent) {
        this.showToastMessage('Please a required consent option to continue');
        return;
      }

      const date: moment.Moment = this.form.value.date;
      const guardianName = this.signaturePadComponent.guardianName;
      const signatoryDelegation = this.signaturePadComponent.signatoryDelegation;


      const consentText = this.compileConsentText();
      const consentResult = this.compileConsentResult();

      const data = {
        ...this.form.value,
        guardianName,
        signatoryDelegation,
        date: date.utc().startOf('day').toISOString(),
        consentResult,
        consentText
      };


      const formData = new FormData();
      const signatureDataUrl = data.signature;
      delete data.signature;
      formData.append('data', JSON.stringify(data));
      if (signatureDataUrl) {
        const signatureBlob = this.dataURLToBlob(signatureDataUrl);
        formData.append('signature', signatureBlob, 'signature.png');
      }

      if (this.editingConsent?.id) {
        this.consentService.updateConsent(this.editingConsent.id, formData)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (result) => {
              this.showToastMessage('Patient consent updated successfully');
              this.returnToParent();
            },
            error: (error) => {
              console.error('Error updating patient consent: ', error);
              this.showToastMessage('There was an error updating the patient consent. Please try again.');
            }
          });
      } else {
        this.consentService.addConsent(formData)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (result) => {
              this.showToastMessage('Patient consent saved successfully');
              this.returnToParent();
            },
            error: (error) => {
              console.error('Error saving patient consent: ', error);
              this.showToastMessage('There was an error saving the patient consent. Please try again.');
            }
          });
      }
    }, 200);

  }

  async deleteConsent() {
    const confirmed = await this.showConfirmationDialog();
    if (!confirmed) return;

    this.consentService.deleteConsent(this.editingConsent.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.showToastMessage('Patient consent deleted successfully');
          this.returnToParent()
        },
        error: (error) => {
          console.error('Error saving patient consent: ', error);
          this.showToastMessage('There was an error deleting the patient consent. Please try again.');
        }
      });
  }

  returnToParent() {
    this.router.navigate(['/patients', { outlets: { patient: ['file', `${this.patient.id}`] } }]);
  }

}
